import React, { useState } from 'react';
import {
  SigeProject, AufstellerInfo, PlanDarstellung, PlanVerantwortlicher,
  DEFAULT_PLAN_FARBEN, FARB_PALETTEN, PLAN_KLEBERAND, PLAN_SCHRIFT, planHatZeitachse, planKleberandMm, planSchriftPt,
  mitPlanKleberand, mitPlanSchrift, mitPlanZeitachse,
} from './types';
import { ReportMetadata } from '../../types';
import { plankopfVerantwortlicheAusProjekt } from './plankopfVerantwortliche';
import {
  gespeichertePlotBreite, mitPlotBreiteZumSpeichern, plotBreitenOptionen, schriftPtText, sigePlanVorlagenFuerAnlage,
  useSigePlanVorlagen,
} from '../../services/sigePlanVorlagen';
import { X, Save, RefreshCw, Trash2, Plus } from 'lucide-react';
import { Button } from '../Button';

interface StempelfeldEditorProps {
  project: SigeProject;
  userProfile: any;
  reportMeta?: ReportMetadata;
  onClose: () => void;
  onSave: (updates: Partial<SigeProject>) => Promise<void>;
}

export const StempelfeldEditor: React.FC<StempelfeldEditorProps> = ({ project, userProfile, reportMeta, onClose, onSave }) => {
  // Plotter-Vorlagen der Firma (globale Einstellungen): Auswahlliste,
  // Standardbreite und Standardschrift.
  const vorlagen = useSigePlanVorlagen();
  const [formData, setFormData] = useState<Partial<SigeProject>>({
    bauherr_name: project.bauherr_name || '',
    bauherr_adresse: project.bauherr_adresse || '',
    baumassnahme_name: project.baumassnahme_name || '',
    baumassnahme_adresse: project.baumassnahme_adresse || '',
    plan_nr: project.plan_nr || '',
    plan_datum: project.plan_datum || new Date().toISOString().split('T')[0],
    plan_inhalt: project.plan_inhalt || 'Sicherheits- und Gesundheitsschutzplan',
    status: project.status || 'entwurf',
    // Im Formular bleibt eine fehlende Breite leer (null) — Auswahl und
    // Hinweis zeigen die Standardbreite der Firma. Gespeichert wird nie null:
    // handleSave setzt dann die aktuelle Standardbreite ein
    // (mitPlotBreiteZumSpeichern; ob die Spalte NULL zulaesst, ist offen).
    plot_breite_mm: gespeichertePlotBreite(project.plot_breite_mm),
    sigeko_name: project.sigeko_name || '',
    sigeko_mobil: project.sigeko_mobil || '',
    sigeko_email: project.sigeko_email || '',
    zeitraum_von: project.zeitraum_von || '',
    zeitraum_bis: project.zeitraum_bis || '',
    bearbeitet_name: project.bearbeitet_name || '',
    geprueft_name: project.geprueft_name || '',
  });
  /**
   * Aufsteller aus den Firmenstammdaten VORBELEGEN.
   *
   * Ein leer gelassenes Aufsteller-Feld druckt im Plankopf den Platzhalter
   * "SiGeKo-Büro" (generatorSigePlan) — auf einem Plan, der an den Bauherrn
   * geht. Die Angaben stehen im Profil bereits; sie hier anzubieten kostet
   * nichts.
   *
   * Nur Vorbelegung: das landet im Formular-State und wird erst durch den
   * Speichern-Button geschrieben. Bereits gepflegte Werte bleiben unangetastet
   * — die Stammdaten fuellen ausschliesslich Luecken.
   */
  const [aufsteller, setAufsteller] = useState<AufstellerInfo>(() => {
    const vorhanden: AufstellerInfo = project.aufsteller || {};
    return {
      ...vorhanden,
      firma: vorhanden.firma || userProfile?.company_name || '',
      adresse: vorhanden.adresse || userProfile?.company_address || '',
      telefon: vorhanden.telefon || userProfile?.settings?.invoiceConfig?.phone || '',
    };
  });
  /**
   * Darstellung: Farben und die Verantwortlichen des Plankopfs (Spalte
   * `sige_projekte.darstellung`, Migration Nr. 62). Leere Farbfelder bedeuten
   * „Vorgabe des Generators" — sie werden deshalb NICHT vorbelegt, sonst
   * friert der erste Speichervorgang die heutige Vorgabe fuer immer ein.
   */
  const [darstellung, setDarstellung] = useState<PlanDarstellung>(() => project.darstellung || {});
  // Eingabetext des Kleberands getrennt vom gespeicherten Wert: sonst sprang
  // ein geleertes Feld beim Tippen sofort auf die Vorgabe zurueck.
  const [kleberandText, setKleberandText] = useState(() => String(planKleberandMm(project.darstellung)));
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const setzeFarben = (f: Partial<PlanDarstellung>) => setDarstellung(alt => ({ ...alt, ...f }));

  const setzeVerantwortlichen = (i: number, v: Partial<PlanVerantwortlicher>) =>
    setDarstellung(alt => ({
      ...alt,
      verantwortliche: (alt.verantwortliche || []).map((e, j) => (j === i ? { ...e, ...v } : e)),
    }));

  const entferneVerantwortlichen = (i: number) =>
    setDarstellung(alt => ({ ...alt, verantwortliche: (alt.verantwortliche || []).filter((_, j) => j !== i) }));

  const ergaenzeVerantwortlichen = () =>
    setDarstellung(alt => ({
      ...alt,
      verantwortliche: [...(alt.verantwortliche || []), { name: '', rolle: (alt.verantwortliche || []).length ? 'Vertretung' : 'SiGeKo' }],
    }));

  /**
   * Koordinator und Vertreter aus den Projekt-Stammdaten uebernehmen.
   * Verantwortliche Planungsphase kommen als „SiGeKo Planung" statt als
   * „Vertretung" (Regel und Begruendung in plankopfVerantwortliche.ts).
   */
  const uebernehmeVerantwortliche = () => {
    const liste = plankopfVerantwortlicheAusProjekt(reportMeta);
    if (!liste.length) return;
    setDarstellung(alt => ({ ...alt, verantwortliche: liste }));
  };

  /** Logo als Data-URL einlesen — jsPDF bekommt es genau so in den Plankopf. */
  const leseLogo = (datei: File) => {
    const leser = new FileReader();
    leser.onload = () => setAufsteller(alt => ({ ...alt, logo_url: String(leser.result) }));
    leser.readAsDataURL(datei);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData({ ...formData, [name]: type === 'number' ? Number(value) : value });
  };

  /**
   * Plotterbreite aus Auswahl oder freiem Feld — immer als Zahl (die Auswahl
   * lieferte frueher Text). Ein geleertes Feld ist im Formular null; beim
   * Speichern wird daraus die Standardbreite der Firma.
   */
  const setzeBreite = (roh: string) => {
    const n = roh.trim() === '' ? NaN : Number(roh);
    setFormData(prev => ({ ...prev, plot_breite_mm: Number.isFinite(n) && n > 0 ? n : null }));
  };
  /** Kleberand aus dem Feld: nur eine lesbare Zahl aendert die Darstellung, ein leeres Feld nicht. */
  const setzeKleberand = (roh: string) => {
    setKleberandText(roh);
    const n = roh.trim() === '' ? NaN : Number(roh.replace(',', '.'));
    if (Number.isFinite(n)) setDarstellung(alt => mitPlanKleberand(alt, n));
  };
  const eigeneBreite = gespeichertePlotBreite(formData.plot_breite_mm);
  const breitenOptionen = plotBreitenOptionen(vorlagen, eigeneBreite);
  const vorgabePt = vorlagen.standardSchriftPt;

  // Stammdaten aus dem Projekt (Projektview) übernehmen.
  // Kein confirm(): Sandbox-iframe blockt Modals; ändert ohnehin nur das Formular,
  // gespeichert wird erst über den Speichern-Button.
  const uebernehmeStammdaten = () => {
    if (!reportMeta) return;
    setFormData(prev => ({
      ...prev,
      bauherr_name: reportMeta.clientName || prev.bauherr_name,
      bauherr_adresse: reportMeta.clientAddress || prev.bauherr_adresse,
      baumassnahme_name: reportMeta.projectName || prev.baumassnahme_name,
      baumassnahme_adresse: reportMeta.projectAddress || prev.baumassnahme_adresse,
      plan_nr: prev.plan_nr || (reportMeta.projectNumber ? `SGP-${reportMeta.projectNumber}` : ''),
      sigeko_name: reportMeta.coordinator || prev.sigeko_name,
      sigeko_mobil: reportMeta.coordinatorPhone || prev.sigeko_mobil,
      sigeko_email: reportMeta.coordinatorEmail || prev.sigeko_email,
      zeitraum_von: prev.zeitraum_von || reportMeta.globalData?.startDate || '',
      zeitraum_bis: prev.zeitraum_bis || reportMeta.globalData?.endDate || '',
      bearbeitet_name: prev.bearbeitet_name || reportMeta.coordinator || '',
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setFehler(null);
    try {
      // Leeres Breitenfeld → Standardbreite der Firma, nie null (siehe oben).
      // Sie wird dann dauerhaft gespeichert — deshalb nach dem Laden der
      // Vorlagen (sigePlanVorlagenFuerAnlage), nicht im Ladeschwebezustand.
      let speicherVorlagen = vorlagen;
      if (gespeichertePlotBreite(formData.plot_breite_mm) === null) {
        const anlage = await sigePlanVorlagenFuerAnlage();
        // Sitzung gewechselt, während gewartet wurde: nicht speichern.
        if (anlage.abgebrochen === true) {
          setFehler('Die Anmeldung hat inzwischen gewechselt.');
          return;
        }
        speicherVorlagen = anlage.vorlagen;
      }
      await onSave(mitPlotBreiteZumSpeichern({ ...formData, aufsteller, darstellung }, speicherVorlagen));
      onClose();
    } catch (e) {
      console.error(e);
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full text-sm border-slate-300 rounded-md p-2 border focus:ring-2 focus:ring-blue-500/20 outline-none';

  return (
    <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">
        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <h2 className="text-lg font-bold text-slate-800">Plankopf / Projektdaten</h2>
          <div className="flex items-center gap-2">
            {reportMeta && (
              <Button variant="outline" size="sm" onClick={uebernehmeStammdaten} title="Bauherr, Baumaßnahme, SiGeKo und Zeitraum aus dem Projekt übernehmen">
                <RefreshCw className="w-4 h-4 mr-2" /> Stammdaten aus Projekt
              </Button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg text-slate-500">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 flex-1 overflow-y-auto space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-500 uppercase">Bauherr</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Name</label>
                <input name="bauherr_name" value={formData.bauherr_name} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Adresse</label>
                <input name="bauherr_adresse" value={formData.bauherr_adresse} onChange={handleChange} className={inputCls} />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-500 uppercase">Baumaßnahme</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Name</label>
                <input name="baumassnahme_name" value={formData.baumassnahme_name} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Adresse</label>
                <input name="baumassnahme_adresse" value={formData.baumassnahme_adresse} onChange={handleChange} className={inputCls} />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-500 uppercase">SiGeKo (Plankopf)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Name</label>
                <input name="sigeko_name" value={formData.sigeko_name} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Mobil</label>
                <input name="sigeko_mobil" value={formData.sigeko_mobil} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">E-Mail</label>
                <input name="sigeko_email" value={formData.sigeko_email} onChange={handleChange} className={inputCls} />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-500 uppercase">Aufsteller (Firma, unten rechts im Plankopf)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Firma</label>
                <input value={aufsteller.firma || ''} onChange={e => setAufsteller({ ...aufsteller, firma: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Adresse</label>
                <input value={aufsteller.adresse || ''} onChange={e => setAufsteller({ ...aufsteller, adresse: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Telefon</label>
                <input value={aufsteller.telefon || ''} onChange={e => setAufsteller({ ...aufsteller, telefon: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">E-Mail</label>
                <input value={aufsteller.email || ''} onChange={e => setAufsteller({ ...aufsteller, email: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Web</label>
                <input value={aufsteller.web || ''} onChange={e => setAufsteller({ ...aufsteller, web: e.target.value })} className={inputCls} />
              </div>
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-28 h-16 border border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden shrink-0">
                {aufsteller.logo_url
                  ? <img src={aufsteller.logo_url} alt="Firmenlogo" className="max-w-full max-h-full object-contain" />
                  : <span className="text-[11px] text-slate-400">kein Logo</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs font-medium text-slate-700 border border-slate-300 rounded-md px-3 py-1.5 cursor-pointer hover:bg-slate-50">
                  Logo waehlen
                  <input type="file" accept="image/png,image/jpeg" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) leseLogo(f); e.target.value = ''; }} />
                </label>
                {userProfile?.company_logo && (
                  <Button variant="outline" size="sm" onClick={() => setAufsteller({ ...aufsteller, logo_url: userProfile.company_logo })}>
                    Firmenlogo uebernehmen
                  </Button>
                )}
                {aufsteller.logo_url && (
                  <Button variant="ghost" size="sm" onClick={() => setAufsteller({ ...aufsteller, logo_url: undefined })}>
                    entfernen
                  </Button>
                )}
                <span className="text-[11px] text-slate-500 w-full">Steht links neben den Firmendaten im Plankopf (PNG oder JPEG).</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm font-semibold text-slate-500 uppercase">Koordination &amp; Vertretung (Plankopf)</h3>
              {reportMeta && (
                <Button variant="outline" size="sm" onClick={uebernehmeVerantwortliche} title="Koordinator und Vertreter aus dem Projekt uebernehmen">
                  <RefreshCw className="w-4 h-4 mr-2" /> Aus Projekt uebernehmen
                </Button>
              )}
            </div>
            {(darstellung.verantwortliche || []).length === 0 && (
              <p className="text-xs text-slate-500">
                Nicht gepflegt &mdash; der Plankopf druckt dann den SiGeKo oben aus diesem Dialog.
              </p>
            )}
            <div className="space-y-2">
              {(darstellung.verantwortliche || []).map((v, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-[7rem,1fr,1fr,1fr,auto] gap-2 items-center">
                  <input className={inputCls} placeholder="Rolle" value={v.rolle || ''}
                    onChange={e => setzeVerantwortlichen(i, { rolle: e.target.value })} />
                  <input className={inputCls} placeholder="Name" value={v.name}
                    onChange={e => setzeVerantwortlichen(i, { name: e.target.value })} />
                  <input className={inputCls} placeholder="Telefon" value={v.telefon || ''}
                    onChange={e => setzeVerantwortlichen(i, { telefon: e.target.value })} />
                  <input className={inputCls} placeholder="E-Mail" value={v.email || ''}
                    onChange={e => setzeVerantwortlichen(i, { email: e.target.value })} />
                  <button onClick={() => entferneVerantwortlichen(i)} className="p-2 text-slate-400 hover:text-red-500" title="Entfernen">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={ergaenzeVerantwortlichen}>
              <Plus className="w-4 h-4 mr-2" /> Zeile hinzufuegen
            </Button>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-500 uppercase">Farben des Plans</h3>
            <div className="flex flex-wrap gap-2">
              {FARB_PALETTEN.map(pal => (
                <button
                  key={pal.name}
                  type="button"
                  onClick={() => setzeFarben(pal.farben)}
                  className="flex items-center gap-2 border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  <span className="w-3.5 h-3.5 rounded-sm" style={{ background: pal.farben.balken }} />
                  <span className="w-3.5 h-3.5 rounded-sm" style={{ background: pal.farben.balkenKritisch }} />
                  {pal.name}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {([
                { key: 'balken' as const, label: 'Zeitbalken', vorgabe: DEFAULT_PLAN_FARBEN.balken },
                { key: 'balkenKritisch' as const, label: 'Balken kritischer Schritte', vorgabe: DEFAULT_PLAN_FARBEN.balkenKritisch },
                { key: 'kopfBand' as const, label: 'Kopfband und Rahmen', vorgabe: DEFAULT_PLAN_FARBEN.kopfBand },
              ]).map(f => (
                <div key={f.key}>
                  <label className="block text-xs text-slate-500 mb-1">{f.label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={darstellung[f.key] || f.vorgabe}
                      onChange={e => setzeFarben({ [f.key]: e.target.value })}
                      className="w-10 h-9 border border-slate-300 rounded-md bg-white p-0.5 cursor-pointer"
                    />
                    <input
                      className={inputCls}
                      placeholder={f.vorgabe}
                      value={darstellung[f.key] || ''}
                      onChange={e => setzeFarben({ [f.key]: e.target.value })}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Leer = Vorgabe. Rot bleibt bewusst den <b>kritischen</b> Schritten vorbehalten &mdash; auf dem Blatt ist Rot
              sonst schon dreifach belegt (Notrufplan, Projektmarker der Karte, Brandschutzzeichen).
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-500 uppercase">Plan Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Plan Nr.</label>
                <input name="plan_nr" value={formData.plan_nr} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Datum</label>
                <input type="date" name="plan_datum" value={formData.plan_datum} onChange={handleChange} className={inputCls} />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs text-slate-500 mb-1">Plan Inhalt</label>
                <input name="plan_inhalt" value={formData.plan_inhalt} onChange={handleChange} className={inputCls} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Bauzeit von (Zeitachse)</label>
                <input type="date" name="zeitraum_von" value={formData.zeitraum_von} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Bauzeit bis (Zeitachse)</label>
                <input type="date" name="zeitraum_bis" value={formData.zeitraum_bis} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">bearbeitet (Name)</label>
                <input name="bearbeitet_name" value={formData.bearbeitet_name} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">geprüft (Name)</label>
                <input name="geprueft_name" value={formData.geprueft_name} onChange={handleChange} className={inputCls} />
              </div>
            </div>

            {/* Je Planversion: in der Planungsphase gibt es noch keinen Bauzeitenplan.
                Gespeichert wird nur der Haken (darstellung.zeitachse = false). */}
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={!planHatZeitachse(darstellung)}
                onChange={e => setDarstellung(alt => mitPlanZeitachse(alt, !e.target.checked))}
              />
              <span>
                Bauzeitenplan liegt noch nicht vor (Planungsphase) – Zeitspalte ohne Balken drucken
                <span className="block text-xs text-slate-500 mt-0.5">
                  Die Spalte „Zeitraum gemeinsamer Nutzung“ bleibt schmal stehen, ohne Monate, Balken und Daten, mit dem
                  Hinweis „Zeiträume folgen mit dem Bauzeitenplan“. Die Bauzeit ist dann keine Pflichtangabe; Daten an
                  den Arbeitsschritten bleiben gespeichert und erscheinen wieder, sobald der Haken entfernt ist.
                </span>
              </span>
            </label>

            <div>
              <label className="block text-xs text-slate-500 mb-1">Status</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="radio" name="status" value="entwurf" checked={formData.status === 'entwurf'} onChange={handleChange} /> Entwurf
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="radio" name="status" value="geprueft" checked={formData.status === 'geprueft'} onChange={handleChange} /> Geprüft
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="radio" name="status" value="ueberarbeitet" checked={formData.status === 'ueberarbeitet'} onChange={handleChange} /> Überarbeitet
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-4 border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold text-slate-500 uppercase">Plotter-Druck</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Plotter-Breite (mm)</label>
                {/* Liste aus den Plotter-Vorlagen der Firma (globale Einstellungen). */}
                <select
                  value={eigeneBreite ?? vorlagen.standardBreite}
                  onChange={e => setzeBreite(e.target.value)}
                  className={inputCls + ' bg-white'}
                >
                  {breitenOptionen.map(o => (
                    <option key={o.mm} value={o.mm}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Eigene Breite (mm, übersteuert die Auswahl)</label>
                <input
                  type="number"
                  value={eigeneBreite ?? ''}
                  onChange={e => setzeBreite(e.target.value)}
                  placeholder={`${vorlagen.standardBreite} (Standard)`}
                  min={300}
                  max={1500}
                  className={inputCls}
                />
              </div>
            </div>
            {eigeneBreite === null && (
              <p className="text-xs text-slate-500 -mt-2">
                Leer = Standardbreite der Firma ({vorlagen.standardBreite} mm) wird übernommen.
              </p>
            )}
            <div>
              <label htmlFor="sige-kleberand" className="block text-xs text-slate-500 mb-1">
                Kleberand oben und unten (mm){darstellung.kleberandMm == null && ' (Standard)'}
              </label>
              <div className="flex items-center gap-3">
                <input
                  id="sige-kleberand"
                  type="number"
                  inputMode="numeric"
                  min={PLAN_KLEBERAND.min}
                  max={PLAN_KLEBERAND.max}
                  step={1}
                  value={kleberandText}
                  onChange={e => setzeKleberand(e.target.value)}
                  onBlur={() => setKleberandText(String(planKleberandMm(darstellung)))}
                  className={inputCls + ' max-w-[8rem]'}
                />
                <button
                  type="button"
                  onClick={() => {
                    setDarstellung(alt => mitPlanKleberand(alt, PLAN_KLEBERAND.vorgabe));
                    setKleberandText(String(PLAN_KLEBERAND.vorgabe));
                  }}
                  disabled={darstellung.kleberandMm == null}
                  className="border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  Standard ({PLAN_KLEBERAND.vorgabe} mm)
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Freier Streifen an der oberen und unteren Blattkante, z. B. zum Aufkleben. 0 = kein Kleberand, es bleibt
                der normale Rand von 8 mm.
              </p>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Schriftgröße der Tabelle: <b className="text-slate-700">{schriftPtText(planSchriftPt(darstellung, vorgabePt))}</b>
                {darstellung.schriftPt == null && ' (Vorgabe)'}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={PLAN_SCHRIFT.min}
                  max={PLAN_SCHRIFT.max}
                  step={PLAN_SCHRIFT.schritt}
                  value={planSchriftPt(darstellung, vorgabePt)}
                  onChange={e => setDarstellung(alt => mitPlanSchrift(alt, Number(e.target.value), vorgabePt))}
                  aria-label="Schriftgröße des Plans"
                  className="flex-1 accent-blue-600"
                />
                <button
                  type="button"
                  onClick={() => setDarstellung(alt => mitPlanSchrift(alt, vorgabePt, vorgabePt))}
                  disabled={darstellung.schriftPt == null}
                  className="border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  Vorgabe ({schriftPtText(vorgabePt)})
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Alle Schriften des Blatts wachsen mit der Tabellenschrift. Die Länge des Plans ergibt sich aus Kopf, Tabelle,
              Randspalte und Kleberand (höchstens 5000 mm) &mdash; größere Schrift macht ihn länger, breiteres Papier kürzer. Die
              Plan-Vorschau zeigt das Blattmaß und hat denselben Regler.
            </p>
          </div>

        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
          {fehler && <p className="mr-auto self-center text-xs text-red-700">Nicht gespeichert: {fehler}</p>}
          <Button variant="ghost" onClick={onClose} disabled={loading}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Wird gespeichert...' : <><Save className="w-4 h-4 mr-2" /> Speichern</>}
          </Button>
        </div>
      </div>
    </div>
  );
};
